import { Fragment, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box, Card, CardContent, Divider, Typography, TextField, Button } from '@mui/material';
import Header from '@/component/Header';
import { Tables } from '@/component/pools/table/Tables';
import { SUCCESS } from '@/lib/common/name-space';
import { readVersionDetail } from '@/api/version/read';
import { ReadVersionType, UpdateVersionType } from '@/types';
import { updateVersionDetail } from '@/api/version/update';

export default function VersionDetail() {
  const {
    state: { versionId },
  } = useLocation();

  const [detail, setDetail] = useState<ReadVersionType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [editedInfo, setEditedInfo] = useState<ReadVersionType | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        // 여기에 실제 API 호출을 추가
        const re = await readVersionDetail(versionId);
        console.log(re);
        if (re.status !== SUCCESS) {
          toast.error(re.message);
          return;
        }
        setDetail(re.data);
        setEditedInfo(re.data);
      } catch (error) {
        console.error('버전 정보 로드 실패:', error);
        toast.error('버전 정보를 불러오는데 실패했습니다.');
      } finally {
        setIsLoading(false);
      }
    })();
    return () => {};
  }, [versionId]);

  const handleSave = async () => {
    if (!editedInfo) return;

    // 버전 번호 유효성 검사
    const currentVersion = detail?.version || '';
    const newVersion = editedInfo.version || '';

    if (newVersion && currentVersion) {
      const currentVersionNum = parseFloat(currentVersion);
      const newVersionNum = parseFloat(newVersion);

      if (isNaN(currentVersionNum) || isNaN(newVersionNum)) {
        toast.error('올바른 버전 번호 형식을 입력해주세요.');
        return;
      }

      if (newVersionNum <= currentVersionNum) {
        toast.error('새로운 버전 번호는 현재 버전보다 커야 합니다.');
        return;
      }
    }

    setIsLoading(true);
    try {
      // 여기에 실제 API 호출을 추가
      const updateVersionType: UpdateVersionType = {
        versionId,
        name: editedInfo?.name,
        version: editedInfo?.version,
        description: editedInfo?.description,
      };
      const re = await updateVersionDetail(updateVersionType);
      if (re.status !== SUCCESS) {
        toast.error(re.message);
        return;
      }

      setDetail((prev) => (prev ? { ...prev, ...editedInfo } : null));
      toast.success('성공');
    } catch (error) {
      toast.error('실패');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFieldChange = (field: keyof ReadVersionType, value: string | string[]) => {
    if (!editedInfo) return;
    setEditedInfo((prev) => (prev ? { ...prev, [field]: value } : null));
  };

  return (
    <>
      <Box sx={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
        <Card>
          <Header title="버전 상세 정보" />
          <Divider />
          <CardContent
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              p: 2,
              backgroundColor: '#f9f9f9',
            }}
          >
            <CardContent>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: '#2c3e50',
                      borderBottom: '2px solid #e0e0e0',
                      pb: 1,
                    }}
                  >
                    버전 정보
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={handleSave}
                      disabled={isLoading}
                      sx={{
                        backgroundColor: '#28a745',
                        '&:hover': { backgroundColor: '#1e7e34' },
                      }}
                    >
                      저장
                    </Button>
                  </Box>
                </Box>

                {detail && (
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 2,
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(300px, 1fr))' },
                    }}
                  >
                    {/* 이름 */}
                    <Card
                      sx={{
                        p: 3,
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e9ecef',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ color: '#6c757d', mb: 1 }}>
                        이름 <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        value={editedInfo?.name || ''}
                        onChange={(e) => handleFieldChange('name', e.target.value)}
                        variant="outlined"
                        size="small"
                      />
                    </Card>
                    {/* 버전 정보 */}
                    <Card
                      sx={{
                        p: 3,
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e9ecef',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ color: '#6c757d', mb: 1 }}>
                        버전 <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        value={editedInfo?.version || ''}
                        onChange={(e) => handleFieldChange('version', e.target.value)}
                        variant="outlined"
                        size="small"
                      />
                    </Card>
                    {/* 업데이트  날짜 */}
                    <Card
                      sx={{
                        p: 3,
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e9ecef',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ color: '#6c757d', mb: 1 }}>
                        업데이트 날짜
                      </Typography>
                      <TextField
                        disabled
                        fullWidth
                        value={editedInfo?.modDt || ''}
                        onChange={(e) => handleFieldChange('modDt', e.target.value)}
                        variant="outlined"
                        size="small"
                      />
                    </Card>

                    {/* 설명 */}
                    <Card
                      sx={{
                        p: 3,
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e9ecef',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        gridColumn: { xs: '1', md: '1 / -1' },
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ color: '#6c757d', mb: 1 }}>
                        설명
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={editedInfo?.description || ''}
                        onChange={(e) => handleFieldChange('description', e.target.value)}
                        variant="outlined"
                        size="small"
                      />
                    </Card>
                  </Box>
                )}
              </Box>
            </CardContent>
          </CardContent>
        </Card>
      </Box>
    </>
  );
}
